import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Wrapper,
  InputWrapper,
  AddressesOptions,
  AddressesOptionsContent,
  EmptyAddressesOptions,
  OptionsContainer,
  OptionsContant,
  Input,
} from "./styles";

import { FiSearch } from "react-icons/fi";

import {
  ICities,
  IContentSelected,
  INeightborhoods,
  IProperties,
} from "./interfaces";

import { useHistory, useLocation } from "react-router-dom";
import OutsideClickHandler from "react-outside-click-handler";
import api from "../../../../../services/api";
import {
  findCityByQuery,
  findNeighborhoodByQuery,
  normalizeSearchText,
} from "./searchBarUtils";
import { NEIGHBORHOOD_CENTERS } from "../../../utils/geoDecisionEngine";

import Portal from './Portal';

const CITIES = [
  { name: "Fortaleza - CE", UF: "CE", city: "Fortaleza" },
  { name: "Recife - PE", UF: "PE", city: "Recife" },
  { name: "São Paulo - SP", UF: "SP", city: "São Paulo" },
  { name: "Salvador - BA", UF: "BA", city: "Salvador" },
  { name: "Teresina - PI", UF: "PI", city: "Teresina" },
  { name: "Maceió - AL", UF: "AL", city: "Maceió" },
];

export default function SearchBar() {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  const history = useHistory();
  const location = useLocation();

  const [cities, setCities] = useState<ICities[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<INeightborhoods[]>([]);
  const [property, setProperty] = useState(null as IProperties | null);
  const [showSearch, setShowSearchs] = useState(false);
  const [inputSearch, setInputSearch] = useState("");
  const [neighborhoodsSearchs, setNeighborhoodsSearchs] = useState<
    INeightborhoods[]
  >([]);
  const [citiesSearchs, setCitiesSearchs] = useState<ICities[]>([]);
  const [loading, setLoading] = useState(true);

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (inputWrapperRef.current) {
        const rect = inputWrapperRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updateDropdownPosition();

    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition);
    };
  }, [inputSearch, showSearch]);

  useEffect(() => {
    try {
      setLoading(true);
      api.get("/api/client/addresses").then((response) => {
        setCities(response.data.data.cities);
        setNeighborhoods(response.data.data.neighborhoods);
        setLoading(false);
      });
    } catch (e) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const neighborhoodResults = neighborhoods.filter((neighborhood) =>
      neighborhood.neighborhood
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .includes(
          inputSearch
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
        )
    );
    setNeighborhoodsSearchs(neighborhoodResults);

    const citiesResults = cities.filter((city) =>
      city.city
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .includes(
          inputSearch
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
        )
    );
    setCitiesSearchs(citiesResults);
  }, [inputSearch, neighborhoods, cities]);

  useEffect(() => {
    try {
      const formatInputSearch = parseInt(inputSearch);

      if (isNaN(formatInputSearch) === false) {
        setLoading(true);
        api
          .get(`/api/client/properties/${inputSearch}`)
          .then((response) => {
            setProperty(response.data.data);
            setLoading(false);
          })
          .catch((err) => {
            setProperty(null);
            setLoading(false);
          });
      }
    } catch (err) {
      setLoading(false);
    }
  }, [inputSearch]);

  const navigateToSearch = useCallback(
    ({ neighborhood, city, UF }: IContentSelected) => {
      const params = new URLSearchParams(location.search);

      params.set("city", city);
      params.set("UF", UF);
      params.delete("lat");
      params.delete("lng");
      params.delete("radius");
      params.delete("zoom");

      if (neighborhood) {
        params.set("neighborhood", neighborhood);
      } else {
        params.delete("neighborhood");
      }

      history.push(`/search?${params.toString()}`);
      setShowSearchs(false);
    },
    [history, location.search]
  );

  const handleSelectContent = useCallback(
    (selection: IContentSelected) => {
      try {
        navigateToSearch(selection);
      } catch (err) {
        console.error("Error in handleSelectContent:", err);
      }
    },
    [navigateToSearch]
  );

  const handleSubmitCode = useCallback(
    (id: number) => {
      const checkNumber = parseInt(inputSearch, 10);
      setShowSearchs(false);

      if (!Number.isNaN(checkNumber)) {
        history.push(`/imovel/${id}`);
      }
    },
    [history, inputSearch]
  );

  const handleSearchSubmit = useCallback(() => {
    const query = inputSearch.trim();
    if (!query) {
      return;
    }

    const numericId = parseInt(query, 10);
    if (!Number.isNaN(numericId) && property?.id) {
      handleSubmitCode(property.id);
      return;
    }

    const currentCity = new URLSearchParams(location.search).get("city");
    const neighborhoodMatch = findNeighborhoodByQuery(
      query,
      neighborhoods,
      currentCity
    );

    if (neighborhoodMatch) {
      navigateToSearch({
        id: 0,
        neighborhood: neighborhoodMatch.neighborhood,
        city: neighborhoodMatch.city,
        UF: neighborhoodMatch.UF,
      });
      return;
    }

    const currentParams = new URLSearchParams(location.search);
    const currentUf = currentParams.get("UF") || currentParams.get("uf") || "CE";
    const staticNeighborhood = Object.keys(NEIGHBORHOOD_CENTERS).find(
      (name) => normalizeSearchText(name) === normalizeSearchText(query)
    );

    if (staticNeighborhood && currentCity) {
      navigateToSearch({
        id: 0,
        neighborhood: staticNeighborhood,
        city: currentCity,
        UF: currentUf,
      });
      return;
    }

    const cityMatch = findCityByQuery(query, citiesSearchs, CITIES);
    if (cityMatch) {
      navigateToSearch({
        id: 0,
        city: cityMatch.city,
        UF: cityMatch.UF,
      });
    }
  }, [
    inputSearch,
    property,
    location.search,
    neighborhoods,
    citiesSearchs,
    navigateToSearch,
    handleSubmitCode,
  ]);

  const handleShowSearch = useCallback(() => {
    setShowSearchs(true);
  }, []);

  const handleNotShowSearch = useCallback(() => {
    setShowSearchs(false);
  }, []);

  return (
    <Wrapper>
      <InputWrapper ref={inputWrapperRef}>
        <FiSearch />
        <Input
          ref={inputRef}
          autoComplete="off"
          list="autocompleteOff"
          onFocus={handleShowSearch}
          value={inputSearch}
          onChange={(e) => {
            setInputSearch(e.target.value);
            handleShowSearch();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearchSubmit();
            }
          }}
          type="text"
          placeholder="Pesquise por cidade, bairro ou código"
        />
      </InputWrapper>

      {showSearch && (
        <Portal>
          <OutsideClickHandler
            onOutsideClick={() => {
              handleNotShowSearch();
            }}
          >
            <AddressesOptions
              style={{
                position: 'fixed',
                top: `${dropdownPosition.top + 10}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
              }}
            >
              <AddressesOptionsContent>
                {cities.length >= 1 && citiesSearchs?.length > 0 && (
                  <OptionsContainer>
                    {(inputSearch !== "" ? citiesSearchs : CITIES).map(
                      (city, index) => (
                        <OptionsContant key={city.city}>
                          <svg
                            width="16"
                            height="21"
                            viewBox="0 0 16 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 10.927C6.9 10.927 6 10.027 6 8.927C6 7.827 6.9 6.927 8 6.927C9.1 6.927 10 7.827 10 8.927C10 10.027 9.1 10.927 8 10.927ZM14 9.127C14 5.497 11.35 2.927 8 2.927C4.65 2.927 2 5.497 2 9.127C2 11.467 3.95 14.567 8 18.267C12.05 14.567 14 11.467 14 9.127ZM8 0.927002C12.2 0.927002 16 4.147 16 9.127C16 12.447 13.33 16.377 8 20.927C2.67 16.377 0 12.447 0 9.127C0 4.147 3.8 0.927002 8 0.927002Z"
                              fill="#DD6536"
                            />
                          </svg>
                          <button
                            type="button"
                            onClick={() =>
                              handleSelectContent({
                                id: index,
                                city: city.city,
                                UF: city.UF,
                              })
                            }
                          >
                            <p>
                              {city.city} - {city.UF}
                            </p>
                          </button>
                        </OptionsContant>
                      )
                    )}
                  </OptionsContainer>
                )}

                {neighborhoods?.length > 0 &&
                  neighborhoodsSearchs?.length > 0 && (
                    <OptionsContainer>
                      {(inputSearch !== ""
                        ? neighborhoodsSearchs
                        : neighborhoods
                      ).map((neighborhood, index) => (
                        <OptionsContant
                          key={`${neighborhood.city}-${neighborhood.neighborhood}`}
                        >
                          <svg
                            width="16"
                            height="21"
                            viewBox="0 0 16 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 10.927C6.9 10.927 6 10.027 6 8.927C6 7.827 6.9 6.927 8 6.927C9.1 6.927 10 7.827 10 8.927C10 10.027 9.1 10.927 8 10.927ZM14 9.127C14 5.497 11.35 2.927 8 2.927C4.65 2.927 2 5.497 2 9.127C2 11.467 3.95 14.567 8 18.267C12.05 14.567 14 11.467 14 9.127ZM8 0.927002C12.2 0.927002 16 4.147 16 9.127C16 12.447 13.33 16.377 8 20.927C2.67 16.377 0 12.447 0 9.127C0 4.147 3.8 0.927002 8 0.927002Z"
                              fill="#DD6536"
                            />
                          </svg>
                          <button
                            type="button"
                            onClick={() =>
                              handleSelectContent({
                                id: index,
                                neighborhood: neighborhood.neighborhood,
                                city: neighborhood.city,
                                UF: neighborhood.UF,
                              })
                            }
                          >
                            <p>
                              {neighborhood.neighborhood}, {neighborhood.city} -{" "}
                              {neighborhood.UF}
                            </p>
                          </button>
                        </OptionsContant>
                      ))}
                    </OptionsContainer>
                  )}

                {property?.address !== undefined && (
                  <OptionsContainer>
                    <OptionsContant key={`${property?.id}`}>
                      <svg
                        width="16"
                        height="21"
                        viewBox="0 0 16 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 10.927C6.9 10.927 6 10.027 6 8.927C6 7.827 6.9 6.927 8 6.927C9.1 6.927 10 7.827 10 8.927C10 10.027 9.1 10.927 8 10.927ZM14 9.127C14 5.497 11.35 2.927 8 2.927C4.65 2.927 2 5.497 2 9.127C2 11.467 3.95 14.567 8 18.267C12.05 14.567 14 11.467 14 9.127ZM8 0.927002C12.2 0.927002 16 4.147 16 9.127C16 12.447 13.33 16.377 8 20.927C2.67 16.377 0 12.447 0 9.127C0 4.147 3.8 0.927002 8 0.927002Z"
                          fill="#DD6536"
                        />
                      </svg>
                      <button
                        key={property.id}
                        type="button"
                        onClick={() => handleSubmitCode(property.id)}
                      >
                        <p>
                          {property.title} ,{property.address.address},{" "}
                          {property.address.city}, {property.address.uf}
                        </p>
                      </button>
                    </OptionsContant>
                  </OptionsContainer>
                )}

                {loading === true && (
                  <EmptyAddressesOptions>
                    <p>Carregando...</p>
                  </EmptyAddressesOptions>
                )}

                {loading !== true &&
                  inputSearch !== "" &&
                  citiesSearchs?.length === 0 &&
                  neighborhoodsSearchs?.length === 0 &&
                  !property && (
                    <EmptyAddressesOptions>
                      <p>Nenhum local encontrado</p>
                    </EmptyAddressesOptions>
                  )}
              </AddressesOptionsContent>
            </AddressesOptions>
          </OutsideClickHandler>
        </Portal>
      )}
    </Wrapper>
  );
}